import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    timeout: 20000,
});

/**
 * Resilient retry interceptor.
 *
 * Cloudflare / pod-hot-reload blips show up as either:
 *   • HTTP 502 / 503 / 504 / 520-524
 *   • A successful 200 carrying an HTML "origin web server returned an invalid
 *     response" page (instead of JSON)
 *   • A network error (no response at all)
 *
 * All three are TRANSIENT — the origin will be back in 1-3 seconds. We auto-
 * retry with exponential backoff (600 ms → 1.5 s → 3 s), and only surface the
 * error to the caller after 3 failed attempts. Mutating verbs (POST/PUT/PATCH/
 * DELETE) are retried too because the backend handlers are idempotent (each
 * either upserts to MongoDB by id or fails the same way every time).
 */
const RETRY_DELAYS_MS = [600, 1500, 3000];
const RETRYABLE_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);

const isCloudflareHtmlError = (resp) => {
    if (!resp) return false;
    const ct = (resp.headers?.["content-type"] || resp.headers?.["Content-Type"] || "").toString().toLowerCase();
    if (!ct.includes("text/html")) return false;
    const body = typeof resp.data === "string" ? resp.data : "";
    return body.includes("Cloudflare") || body.includes("origin web server") || body.includes("Error 5");
};

api.interceptors.response.use(
    (response) => {
        // Some Cloudflare blips return 200 OK with an HTML error page. Treat as failure.
        if (isCloudflareHtmlError(response)) {
            const err = new Error("Cloudflare returned origin-error HTML instead of JSON");
            err.response = response;
            err.config = response.config;
            return Promise.reject(err);
        }
        return response;
    },
    async (error) => {
        const cfg = error?.config || {};
        cfg.__retryCount = cfg.__retryCount || 0;
        const status = error?.response?.status || 0;
        const isHtmlBlip = isCloudflareHtmlError(error?.response);
        const isRetryable = RETRYABLE_STATUS.has(status) || isHtmlBlip || error?.code === "ECONNABORTED" || error?.code === "ERR_NETWORK";
        if (isRetryable && cfg.__retryCount < RETRY_DELAYS_MS.length) {
            const delay = RETRY_DELAYS_MS[cfg.__retryCount];
            cfg.__retryCount += 1;
            console.info(`[api] transient error (${status || error?.code || "?"}), retrying in ${delay}ms (attempt ${cfg.__retryCount}/${RETRY_DELAYS_MS.length})…`);
            await new Promise((r) => setTimeout(r, delay));
            return api(cfg);
        }
        return Promise.reject(error);
    },
);
